import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '@/utils/api-request';
import { 
    Search, 
    ChevronLeft, 
    ChevronRight, 
    ChevronsLeft, 
    ChevronsRight, 
    Users, 
    CheckCircle, 
    XCircle, 
    Clock, 
    Eye, 
    FileText, 
    Phone, 
    Mail,
    Briefcase,
    Calendar,
    Download
} from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';
import SettingsSlideModal from '@/components/Modal/SettingsSlideModal';

interface UserInfo {
    userId: number;
    uId: string;
    name: string;
    email: string;
    phone: string;
}

interface JobInfo {
    job_id: number;
    title: string;
    category: string;
    teaching_category: string | null;
}

interface ExperienceInfo {
    years: string;
    months: string | null;
    total: string;
}

interface Application {
    application_id: number;
    user_id: number;
    user: UserInfo;
    job: JobInfo;
    experience: ExperienceInfo | null;
    current_salary: number | null;
    resume: string | null;
    views: number;
    status: 'applied' | 'shortlisted' | 'rejected';
    is_shortlisted: boolean;
    is_rejected: boolean;
    applied_date: string;
    updated_date: string;
    shortlisted_date: string | null;
    rejected_date: string | null;
}

interface Summary {
    total: number;
    applied: number;
    shortlisted: number;
    rejected: number;
}

interface PaginationData {
    totalApplications: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
}

interface Job {
    id: number;
    title: string;
}

const CandidateList: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const schoolId = id ? parseInt(id) : null;

    const [applications, setApplications] = useState<Application[]>([]);
    const [summary, setSummary] = useState<Summary>({ total: 0, applied: 0, shortlisted: 0, rejected: 0 });
    const [pagination, setPagination] = useState<PaginationData>({
        totalApplications: 0,
        totalPages: 1,
        currentPage: 1,
        pageSize: 25
    });
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [jobFilter, setJobFilter] = useState<string>('');
    const [jobs, setJobs] = useState<Job[]>([]);

    const [selectedCandidate, setSelectedCandidate] = useState<Application | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);

    // Extract unique jobs from applications for dropdown
    const extractJobsFromApplications = (apps: Application[]) => {
        const jobMap = new Map<number, Job>();
        apps.forEach(app => {
            if (app.job && app.job.job_id && !jobMap.has(app.job.job_id)) {
                jobMap.set(app.job.job_id, {
                    id: app.job.job_id,
                    title: app.job.title
                });
            }
        });
        return Array.from(jobMap.values());
    };

    // Fetch all applications initially to get jobs list for dropdown
    const fetchJobsForDropdown = async (): Promise<void> => {
        if (!schoolId) return;
        try {
            const response = await apiRequest(`/api/jobs/schoolapplications/${schoolId}`, {
                method: 'POST',
                body: JSON.stringify({ page: 1, limit: 1000 }), // Get all to extract unique jobs
            });
            if (response.success && response.data?.applications) {
                const uniqueJobs = extractJobsFromApplications(response.data.applications);
                setJobs(uniqueJobs);
            }
        } catch (error) {
            console.error('Error fetching jobs for dropdown:', error);
        }
    };

    const fetchApplications = async (page: number = 1): Promise<void> => {
        if (!schoolId) {
            showToast('School ID is required', 'error');
            return;
        }

        setIsLoading(true);
        try {
            const payload: Record<string, unknown> = {
                page,
                limit: pagination.pageSize,
            };

            if (searchTerm.trim()) {
                payload.search = searchTerm.trim();
            }
            if (statusFilter) {
                payload.status_filter = statusFilter;
            }
            if (jobFilter) {
                payload.job_id = parseInt(jobFilter);
            }

            const response = await apiRequest(`/api/jobs/schoolapplications/${schoolId}`, {
                method: 'POST',
                body: JSON.stringify(payload),
            });

            if (response.success) {
                setApplications(response.data?.applications || []);
                setSummary(response.data?.summary || { total: 0, applied: 0, shortlisted: 0, rejected: 0 });
                setPagination(response.data?.pagination || {
                    totalApplications: 0,
                    totalPages: 1,
                    currentPage: page,
                    pageSize: 25
                });
            } else {
                showToast('Failed to fetch applications', 'error');
                setApplications([]);
            }
        } catch (error) {
            console.error('Error fetching applications:', error);
            showToast('Failed to fetch applications', 'error');
            setApplications([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (schoolId) {
            fetchJobsForDropdown();
            fetchApplications(1);
        }
    }, [schoolId]);

    useEffect(() => {
        if (!schoolId) return;
        const debounce = setTimeout(() => {
            fetchApplications(1);
        }, 500);
        return () => clearTimeout(debounce);
    }, [searchTerm, statusFilter, jobFilter]);

    const handlePageChange = (newPage: number): void => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchApplications(newPage);
        }
    };

    const handleCandidateClick = (application: Application): void => {
        setSelectedCandidate(application);
        setIsModalOpen(true);
    };

    const handleCloseModal = (): void => {
        setIsModalOpen(false);
        setSelectedCandidate(null);
    };

    const updateApplicationStatus = async (action: 'shortlist' | 'reject' | 'reset'): Promise<void> => {
        if (!selectedCandidate) return;

        setIsUpdatingStatus(true);
        try {
            const response = await apiRequest(`/api/jobs/applications/${selectedCandidate.application_id}/status`, {
                method: 'PUT',
                body: JSON.stringify({ action }),
            });

            if (response.success) {
                let message = 'Status updated successfully';
                if (action === 'shortlist') {
                    message = 'Candidate shortlisted successfully';
                } else if (action === 'reject') {
                    message = 'Candidate rejected';
                } else {
                    message = 'Status reset successfully';
                }
                showToast(message, 'success');
                
                let newStatus: 'applied' | 'shortlisted' | 'rejected' = 'applied';
                if (action === 'shortlist') {
                    newStatus = 'shortlisted';
                } else if (action === 'reject') {
                    newStatus = 'rejected';
                }
                setSelectedCandidate({ ...selectedCandidate, status: newStatus });
                fetchApplications(pagination.currentPage);
            } else {
                showToast(response.message || 'Failed to update status', 'error');
            }
        } catch (error) {
            console.error('Error updating application status:', error);
            showToast('Failed to update status', 'error');
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const formatDate = (dateString: string | null | undefined): string => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
        const timeStr = date.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        }).replace(/am/i, 'AM').replace(/pm/i, 'PM');
        return `${dateStr}, ${timeStr}`;
    };

    const formatSalary = (salary: number | null | undefined): string => {
        if (!salary) return '-';
        return `₹${salary.toLocaleString('en-IN')}`;
    };

    const getUserInitial = (name: string | undefined | null): string => {
        if (!name) return 'U';
        return name.charAt(0).toUpperCase();
    };

    const truncateText = (text: string | undefined | null, maxLength: number = 30): string => {
        if (!text) return '-';
        if (text.length > maxLength) {
            return text.substring(0, maxLength) + '...';
        }
        return text;
    };

    const getStatusBadge = (status: string) => {
        if (status === 'shortlisted') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">
                    <CheckCircle size={12} />
                    Shortlisted
                </span>
            );
        }
        if (status === 'rejected') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">
                    <XCircle size={12} />
                    Rejected
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                <Clock size={12} />
                Applied
            </span>
        );
    };

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-8 py-4">
                <div className="flex items-center gap-4">
                    {/* Left Side - Icon, Title with Badge */}
                    <div className="flex items-center gap-3" style={{ flex: '0 0 25%' }}>
                        <div className="flex-shrink-0">
                            <div className="p-2 bg-brandColor rounded-lg text-white">
                                <Users className="h-5 w-5" />
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                                <h1 className="text-lg font-semibold text-gray-900">Candidates</h1>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brandColor text-white">
                                    {summary.total.toLocaleString()}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500">Manage job applications for this school</p>
                        </div>
                    </div>

                    {/* Right Side - Search and Filters */}
                    <div className="flex items-center gap-3 justify-end" style={{ flex: '0 0 75%' }}>
                        <div className="relative" style={{ width: '250px' }}>
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search candidates..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">All Status</option>
                            <option value="applied">Applied</option>
                            <option value="shortlisted">Shortlisted</option>
                            <option value="rejected">Rejected</option>
                        </select>

                        <select
                            value={jobFilter}
                            onChange={(e) => setJobFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent max-w-[200px]"
                        >
                            <option value="">All Jobs</option>
                            {jobs.map((job) => (
                                <option key={job.id} value={job.id}>{truncateText(job.title, 25)}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="px-8 py-4">
                <div className="grid grid-cols-4 gap-4">
                    <div 
                        className={`bg-white rounded-lg border p-4 cursor-pointer transition-all ${statusFilter === '' ? 'border-brandColor ring-2 ring-brandColor/20' : 'border-gray-200 hover:border-gray-300'}`}
                        onClick={() => setStatusFilter('')}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">Total</p>
                                <p className="text-2xl font-bold text-gray-900">{summary.total}</p>
                            </div>
                            <div className="p-3 bg-gray-100 rounded-full">
                                <Users className="h-5 w-5 text-gray-600" />
                            </div>
                        </div>
                    </div>
                    <div 
                        className={`bg-white rounded-lg border p-4 cursor-pointer transition-all ${statusFilter === 'applied' ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-200 hover:border-gray-300'}`}
                        onClick={() => setStatusFilter(statusFilter === 'applied' ? '' : 'applied')}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">Applied</p>
                                <p className="text-2xl font-bold text-blue-600">{summary.applied}</p>
                            </div>
                            <div className="p-3 bg-blue-100 rounded-full">
                                <Clock className="h-5 w-5 text-blue-600" />
                            </div>
                        </div>
                    </div>
                    <div 
                        className={`bg-white rounded-lg border p-4 cursor-pointer transition-all ${statusFilter === 'shortlisted' ? 'border-green-500 ring-2 ring-green-500/20' : 'border-gray-200 hover:border-gray-300'}`}
                        onClick={() => setStatusFilter(statusFilter === 'shortlisted' ? '' : 'shortlisted')}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">Shortlisted</p>
                                <p className="text-2xl font-bold text-green-600">{summary.shortlisted}</p>
                            </div>
                            <div className="p-3 bg-green-100 rounded-full">
                                <CheckCircle className="h-5 w-5 text-green-600" />
                            </div>
                        </div>
                    </div>
                    <div 
                        className={`bg-white rounded-lg border p-4 cursor-pointer transition-all ${statusFilter === 'rejected' ? 'border-red-500 ring-2 ring-red-500/20' : 'border-gray-200 hover:border-gray-300'}`}
                        onClick={() => setStatusFilter(statusFilter === 'rejected' ? '' : 'rejected')}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">Rejected</p>
                                <p className="text-2xl font-bold text-red-600">{summary.rejected}</p>
                            </div>
                            <div className="p-3 bg-red-100 rounded-full">
                                <XCircle className="h-5 w-5 text-red-600" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto px-4">
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full table-auto border-collapse">
                        <thead className="bg-brandColor text-white sticky top-0 z-10">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Candidate</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Job Applied</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Experience</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Current Salary</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Applied Date</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Status</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                                        Loading candidates...
                                    </td>
                                </tr>
                            ) : applications.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                                        No applications found for this school
                                    </td>
                                </tr>
                            ) : (
                                applications.map((app, index) => (
                                    <tr
                                        key={app.application_id}
                                        className={`hover:bg-blue-50 cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                                        onClick={() => handleCandidateClick(app)}
                                    >
                                        <td className="px-4 py-2 text-sm">
                                            <div className="flex items-center gap-3">
                                                <div className="h-9 w-9 rounded-full bg-brandColor/10 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-brandColor font-semibold text-sm">
                                                        {getUserInitial(app.user?.name)}
                                                    </span>
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-medium text-gray-900 truncate">{app.user?.name || 'N/A'}</p>
                                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                                        <span className="flex items-center gap-1">
                                                            <Mail size={10} />
                                                            {truncateText(app.user?.email, 20)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm">
                                            <div>
                                                <p className="font-medium text-gray-900">{truncateText(app.job?.title, 25)}</p>
                                                <p className="text-xs text-gray-500">{app.job?.category || ''}</p>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {app.experience?.total || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {formatSalary(app.current_salary)}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">
                                            {formatDate(app.applied_date)}
                                        </td>
                                        <td className="px-4 py-2 text-sm">
                                            {getStatusBadge(app.status)}
                                        </td>
                                        <td className="px-4 py-2 text-sm">
                                            <div className="flex items-center justify-center gap-1">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCandidateClick(app);
                                                    }}
                                                    className="p-1.5 text-gray-500 hover:text-brandColor hover:bg-brandColor/10 rounded transition-colors"
                                                    title="View Details"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                {app.resume && (
                                                    
                                                       <a href={app.resume}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                        title="View Resume"
                                                    >
                                                        <FileText size={16} />
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <div className="text-sm text-gray-600">
                    Showing {pagination.totalApplications === 0 ? 0 : ((pagination.currentPage - 1) * pagination.pageSize) + 1} to{' '}
                    {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalApplications)} of{' '}
                    {pagination.totalApplications} results
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        disabled={pagination.currentPage === 1}
                    >
                        <ChevronsLeft size={16} />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                    >
                        <ChevronLeft size={16} />
                    </Button>
                    <span className="text-sm text-gray-600">
                        Page {pagination.currentPage} of {pagination.totalPages || 1}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
                    >
                        <ChevronRight size={16} />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.totalPages)}
                        disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
                    >
                        <ChevronsRight size={16} />
                    </Button>
                </div>
            </div>

            {/* Candidate Detail Modal */}
            <SettingsSlideModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title="Candidate Details"
                subtitle={selectedCandidate ? `Application #${selectedCandidate.application_id}` : ''}
                icon={<div className="text-brandColor"><Users size={24} /></div>}
                width="700px"
            >
                {selectedCandidate && (
                    <>
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {/* Candidate Info */}
                            <div className="flex items-start gap-4">
                                <div className="h-16 w-16 rounded-full bg-brandColor/10 flex items-center justify-center flex-shrink-0">
                                    <span className="text-brandColor text-2xl font-semibold">
                                        {getUserInitial(selectedCandidate.user?.name)}
                                    </span>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-xl font-semibold text-gray-900">
                                        {selectedCandidate.user?.name || 'N/A'}
                                    </h3>
                                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                                        <span className="flex items-center gap-1">
                                            <Mail size={14} />
                                            {selectedCandidate.user?.email || 'N/A'}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Phone size={14} />
                                            {selectedCandidate.user?.phone || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="mt-2">
                                        {getStatusBadge(selectedCandidate.status)}
                                    </div>
                                </div>
                            </div>

                            {/* Job Info */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h4 className="text-xs text-gray-500 uppercase mb-2">Applied For</h4>
                                <p className="text-lg font-semibold text-gray-900">
                                    {selectedCandidate.job?.title || 'N/A'}
                                </p>
                                <p className="text-sm text-gray-600">
                                    {selectedCandidate.job?.category || ''}
                                    {selectedCandidate.job?.teaching_category ? ` • ${selectedCandidate.job.teaching_category}` : ''}
                                </p>
                            </div>

                            {/* Details Grid */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-gray-500 mb-1">
                                        <Briefcase size={14} />
                                        <span className="text-xs uppercase">Experience</span>
                                    </div>
                                    <p className="text-lg font-semibold text-gray-900">
                                        {selectedCandidate.experience?.total || 'N/A'}
                                    </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-gray-500 mb-1">
                                        <span className="text-xs">💰</span>
                                        <span className="text-xs uppercase">Current Salary</span>
                                    </div>
                                    <p className="text-lg font-semibold text-gray-900">
                                        {formatSalary(selectedCandidate.current_salary)}
                                    </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-gray-500 mb-1">
                                        <Calendar size={14} />
                                        <span className="text-xs uppercase">Applied On</span>
                                    </div>
                                    <p className="text-lg font-semibold text-gray-900">
                                        {formatDate(selectedCandidate.applied_date)}
                                    </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-gray-500 mb-1">
                                        <Eye size={14} />
                                        <span className="text-xs uppercase">Profile Views</span>
                                    </div>
                                    <p className="text-lg font-semibold text-gray-900">
                                        {selectedCandidate.views || 0}
                                    </p>
                                </div>
                            </div>

                            {/* Resume */}
                            {selectedCandidate.resume && (
                                <div>
                                    <h4 className="text-xs text-gray-500 uppercase mb-2">Resume</h4>
                                    
                                       <a href={selectedCandidate.resume}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 px-4 py-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                                    >
                                        <Download size={16} />
                                        <span>Download Resume</span>
                                    </a>
                                </div>
                            )}

                            {/* Timeline */}
                            <div className="border-t pt-4">
                                <h4 className="text-xs text-gray-500 uppercase mb-3">Timeline</h4>
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                            <Clock size={14} className="text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">Applied</p>
                                            <p className="text-xs text-gray-500">{formatDate(selectedCandidate.applied_date)}</p>
                                        </div>
                                    </div>
                                    {selectedCandidate.shortlisted_date && (
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                                                <CheckCircle size={14} className="text-green-600" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">Shortlisted</p>
                                                <p className="text-xs text-gray-500">{formatDate(selectedCandidate.shortlisted_date)}</p>
                                            </div>
                                        </div>
                                    )}
                                    {selectedCandidate.rejected_date && (
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
                                                <XCircle size={14} className="text-red-600" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">Rejected</p>
                                                <p className="text-xs text-gray-500">{formatDate(selectedCandidate.rejected_date)}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Fixed Footer */}
                        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                            <div className="flex gap-3 justify-between">
                                <div className="flex gap-2">
                                    {selectedCandidate.status !== 'rejected' && (
                                        <Button
                                            variant="outline"
                                            onClick={() => updateApplicationStatus('reject')}
                                            disabled={isUpdatingStatus}
                                            className="border-red-300 text-red-600 hover:bg-red-50"
                                        >
                                            <XCircle size={16} className="mr-2" />
                                            Reject
                                        </Button>
                                    )}
                                    {(selectedCandidate.status === 'shortlisted' || selectedCandidate.status === 'rejected') && (
                                        <Button
                                            variant="outline"
                                            onClick={() => updateApplicationStatus('reset')}
                                            disabled={isUpdatingStatus}
                                        >
                                            Reset Status
                                        </Button>
                                    )}
                                </div>
                                <div className="flex gap-3">
                                    {selectedCandidate.status !== 'shortlisted' && (
                                        <Button
                                            onClick={() => updateApplicationStatus('shortlist')}
                                            disabled={isUpdatingStatus}
                                            className="bg-green-600 hover:bg-green-700 text-white"
                                        >
                                            <CheckCircle size={16} className="mr-2" />
                                            Shortlist
                                        </Button>
                                    )}
                                    <Button variant="outline" onClick={handleCloseModal}>
                                        Close
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </SettingsSlideModal>
        </div>
    );
};

export default CandidateList;